import * as stylex from '@stylexjs/stylex'
import { useEffect, useId, useMemo, useState } from "react";

import Button from "./Button";
import Dialog from "./Dialog";
import Avatar from "./Avatar";
import Icon from "./Icon";
import { FormField, Input, Select, Textarea } from "./FormControls";
import SearchInput from "./SearchInput";
import SegmentedControl from "./SegmentedControl";
import TransactionDateTimeField from "./TransactionDateTimeField";
import MoneyInput from './MoneyInput';
import {
  combineTransactionDateTime,
  equalShares,
  findGroup,
  findPerson,
  parseCents,
  currencyOptions,
  formatMoney,
  localDateInputValue,
  type CreateExpenseInput,
  type Overview,
} from "../lib/ledger";
import { itemizedSplits } from '../lib/itemizedSplits';


const styles = stylex.create({
  sectionTitle: { margin: 0, fontSize: '15px', fontWeight: 700, color: '#315c3d' },
  extra: { border: '1px solid #bacab6', borderRadius: '12px', padding: '12px', backgroundColor: '#e7eee2' },
  extraSummary: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '44px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: '#315c3d', ':focus-visible': { outline: '2px solid #397a4b', outlineOffset: '2px' } },
  extraContent: { paddingTop: '12px' },
  controlSurface: {
    borderColor: "#87978a",
    backgroundColor: "#fff",
    color: "#27342b",
    transition: "border-color 150ms, box-shadow 150ms",
    "::placeholder": { color: "#657268", opacity: 1 },
    ":hover": { borderColor: "#5f7665" },
    ":focus-within": { borderColor: "#397a4b", boxShadow: "0 0 0 3px rgb(57 122 75 / 20%)" },
    ":focus": { borderColor: "#397a4b", boxShadow: "0 0 0 3px rgb(57 122 75 / 20%)" },
  },
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
    minWidth: 0,
    padding: 0,
    margin: 0,
  },
  s1: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "minmax(0,1fr) 220px",
    },
  },
  s2: {
    display: "block",
  },
  s3: {
    marginBottom: "8px",
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s4: {
    position: "relative",
  },
  s5: {
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "14px",
    fontSize: "16px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s6: {
    position: "absolute",
    zIndex: "30",
    marginTop: "8px",
    maxHeight: "224px",
    width: "100%",
    overflowY: "auto",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    padding: "6px",
    boxShadow: "0 10px 24px rgba(48,70,51,.14)",
  },
  s7: {
    display: "flex",
    height: "48px",
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":focus-within": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s8: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "14px",
    fontSize: "22px",
    fontWeight: 700,
    letterSpacing: "-.02em",
    backgroundColor: "transparent",
    color: "#27342b",
    outline: "none",
    "::placeholder": {
      fontWeight: 400,
      color: "#657268",
      opacity: 1,
    },
  },
  s9: {
    borderLeftWidth: 1,
    borderColor: "#87978a",
    backgroundColor: "#e7eee2",
    paddingInline: "10px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#3d6247",
    outline: "none",
  },
  s13: {
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s17: {
    paddingInline: "12px",
    paddingBlock: "8px",
    fontSize: "12px",
    color: "#748077",
  },
  s18: {
    minWidth: "0px",
    flex: "1 1 0%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  s19: {
    height: "16px",
    width: "16px",
    color: "#4d9460",
  },
  s22: {
    position: "absolute",
    zIndex: "30",
    marginTop: "8px",
    maxHeight: "224px",
    width: "100%",
    overflowY: "auto",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    padding: "6px",
    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  s23: {
    flex: "1 1 0%",
  },
  s24: {
    marginTop: "8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  s25: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: "0.5rem",
    backgroundColor: "#edf7eb",
    paddingInline: "8px",
    paddingBlock: "4px",
    maxWidth: "100%",
    fontSize: "11px",
    fontWeight: 600,
    color: "#3e7d4d",
  },
  s26: {
    display: "flex",
    minHeight: "44px",
    minWidth: "44px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    fontSize: "14px",
    lineHeight: 1,
    color: "#5d8064",
    ":hover": {
      backgroundColor: "#d8ecd5",
    },
  },
  s27: {
    marginTop: "8px",
    fontSize: "11px",
    color: "#59665d",
  },
  s28: {
    minWidth: 0,
    margin: 0,
    padding: 0,
  },
  s30: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  s31: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  s32: {
    height: "44px",
    minWidth: "0px",
    flex: "1 1 0%",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#87978a",
    paddingInline: "10px",
    fontSize: "16px",
    width: "100%",
  },
  s33: {
    height: "44px",
    minWidth: 0,
    width: "100%",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#87978a",
    paddingInline: "10px",
    textAlign: "right",
    fontSize: "16px",
  },
  s46: {
    height: "44px",
    minWidth: 0,
    width: "100%",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#87978a",
    paddingInline: "8px",
    fontSize: "16px",
  },
  s34: {
    height: "44px",
    width: "44px",
    borderRadius: "0.5rem",
    fontSize: "1.125rem",
    color: "#7b887d",
    ":hover": {
      backgroundColor: "#edf5eb",
    },
  },
  s35: {
    minHeight: "44px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#3e7d4d",
  },
  s36: {
    minWidth: "0px",
    flex: "1 1 0%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "12px",
    color: "#526057",
  },
  s37: {
    display: "flex",
    height: "44px",
    width: "144px",
    alignItems: "center",
    overflow: "hidden",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
  },
  s38: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "10px",
    textAlign: "right",
    fontSize: "16px",
    backgroundColor: "transparent",
    outline: "none",
    "::placeholder": { color: "#657268", opacity: 1 },
  },
  s39: {
    paddingInline: "8px",
    fontSize: "11px",
    color: "#7a887d",
  },
  s40: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#f0d0c0",
    backgroundColor: "#fff5f0",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    color: "#9b491e",
  },
  s44: {
    height: "16px",
    width: "16px",
  },
  contextGrid: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    },
  },
  quickDateRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "8px",
  },
  quickDateButton: {
    minHeight: "32px",
    borderRadius: "9999px",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    paddingInline: "11px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#607068",
    backgroundColor: "#fff",
    ":hover": { backgroundColor: "#f2f8f1", color: "#3e7d4d" },
  },
  quickDateSelected: {
    borderColor: "#b9d9bd",
    backgroundColor: "#edf7eb",
    color: "#397a4b",
  },
  summaryButton: {
    display: "flex",
    width: "100%",
    minHeight: "56px",
    alignItems: "center",
    gap: "12px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "12px",
    paddingBlock: "8px",
    textAlign: "left",
    transition: "border-color 150ms, background-color 150ms, box-shadow 150ms",
    ":hover": { backgroundColor: "#f8fbf7", borderColor: "#c8d8c8" },
    ":focus-visible": { outline: "2px solid #3f8654", outlineOffset: "2px" },
  },
  summaryButtonOpen: {
    borderColor: "#5b9a6c",
    boxShadow: "0 0 0 3px rgb(91 154 108 / 10%)",
  },
  summaryMain: { minWidth: 0, flex: "1 1 0%" },
  summaryTitle: {
    display: "block",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontWeight: 600,
    color: "#2f3d33",
  },
  summaryMeta: {
    display: "block",
    marginTop: "2px",
    fontSize: "11px",
    color: "#59665d",
  },
  summaryChevron: { flexShrink: 0, color: "#718073" },
  splitConfiguration: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#bacab6",
    backgroundColor: "#e7eee2",
    padding: "14px",
  },
  fieldError: {
    marginTop: "6px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#a34e29",
  },
  splitStatus: {
    marginTop: "10px",
    borderRadius: "0.65rem",
    paddingInline: "10px",
    paddingBlock: "8px",
    fontSize: "11px",
    fontWeight: 600,
  },
  splitStatusValid: { backgroundColor: "#edf7eb", color: "#397a4b" },
  splitStatusInvalid: { backgroundColor: "#fff4ee", color: "#9b491e" },
  footerActions: {
    display: "grid",
    gridTemplateColumns: "minmax(0, .8fr) minmax(0, 1.4fr)",
    gap: "8px",
    "@media (min-width: 640px)": { gridTemplateColumns: "auto auto", justifyContent: "end" },
  },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 44px",
    gap: "8px",
    padding: "10px",
    border: "1px solid #dbe2d9",
    borderRadius: "12px",
    backgroundColor: "#f3f2eb",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr) 100px 44px",
    },
  },
  itemName: {
    gridColumn: "1 / -1",
    "@media (min-width: 640px)": { gridColumn: "auto" },
  },
  splitSummary: { marginTop: "12px", borderTop: "1px solid #dbe2d9", paddingTop: "8px" },
  summaryRow: { display: "flex", justifyContent: "space-between", gap: "12px", fontSize: "13px", paddingBlock: "6px", overflowWrap: "anywhere" },
})

const expenseDialogStyles = stylex.create({
  option: { display: 'flex', width: '100%', alignItems: 'center', gap: '8px', borderRadius: '0.5rem', paddingInline: '12px', paddingBlock: '8px', textAlign: 'left', fontSize: '13px', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  optionSelected: { backgroundColor: '#edf7eb', fontWeight: 600, color: '#3e7d4d' },
  optionIdle: { color: '#46544a', ':hover': { backgroundColor: '#f5faf3' } },
  participantIdle: { ':hover': { backgroundColor: '#edf7eb' } },
})
type SplitType = "equal" | "exact" | "percentage" | "shares" | "itemized";
type ReceiptItem = { id: number; name: string; amount: string; assignedTo: string };

type ExpenseDialogProps = {
  data: Overview;
  defaultGroupID?: string;
  defaultPersonID?: string;
  onClose: () => void;
  onSave: (input: CreateExpenseInput) => Promise<void>;
};

export default function ExpenseDialog({
  data,
  defaultGroupID,
  defaultPersonID,
  onClose,
  onSave,
}: ExpenseDialogProps) {
  const formID = useId();
  const initialGroupID = defaultGroupID ?? "";
  const initialGroup = findGroup(data, initialGroupID);
  const initialParticipantIDs = initialGroup
    ? Array.from(new Set(initialGroup.memberIds)).filter(
        (id) => id !== data.currentUser.id,
      )
    : Array.from(
        new Set(
          [defaultPersonID].filter(
            (id): id is string => Boolean(id) && id !== data.currentUser.id,
          ),
        ),
      );
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(() => localDateInputValue(new Date()));
  const [expenseTime, setExpenseTime] = useState("");
  const [currency, setCurrency] = useState(data.currentUser.currency);
  const [groupID, setGroupID] = useState(initialGroupID);
  const [paidByID, setPaidByID] = useState(data.currentUser.id);
  const [payerOpen, setPayerOpen] = useState(false);
  const [payerQuery, setPayerQuery] = useState("");
  const [participantIDs, setParticipantIDs] = useState<string[]>(initialParticipantIDs);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitConfigurationOpen, setSplitConfigurationOpen] = useState(false);
  const [splitQuery, setSplitQuery] = useState("");
  const [splitType, setSplitType] = useState<SplitType>("equal");
  const [splitValues, setSplitValues] = useState<Record<string, string>>({});
  const [items, setItems] = useState<ReceiptItem[]>([
    { id: 1, name: "", amount: "", assignedTo: "" },
  ]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  useEffect(() => {
    const closeDropdowns = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest("[data-dropdown]")) {
        setDescriptionOpen(false);
        setPayerOpen(false);
        setSplitOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeDropdowns);
    document.addEventListener("click", closeDropdowns);
    document.addEventListener("focusin", closeDropdowns);
    return () => {
      document.removeEventListener("pointerdown", closeDropdowns);
      document.removeEventListener("click", closeDropdowns);
      document.removeEventListener("focusin", closeDropdowns);
    };
  }, []);

  const group = findGroup(data, groupID);
  const members = group
    ? Array.from(new Set([data.currentUser.id, ...group.memberIds]))
    : Array.from(
        new Set([data.currentUser.id, ...data.people.map((person) => person.id)]),
      );
  const filteredMembers = members.filter((memberID) => {
    const person = findPerson(data, memberID);
    return (memberID === data.currentUser.id ? "you" : (person?.name ?? ""))
      .toLowerCase()
      .includes(payerQuery.toLowerCase());
  });
  const selectedPayer = findPerson(data, paidByID);
  const filteredSplitMembers = members
    .filter((memberID) => {
      const person = findPerson(data, memberID);
      return (memberID === data.currentUser.id ? "you" : (person?.name ?? ""))
        .toLowerCase()
        .includes(splitQuery.toLowerCase());
    });
  const descriptionSuggestions = Array.from(
    new Set(
      data.expenses
        .map((expense) => expense.description.trim())
        .filter(Boolean),
    ),
  )
    .filter((value) => value.toLowerCase().includes(description.toLowerCase()))
    .sort((left, right) => left.localeCompare(right))
    .slice(0, 8);
  const amountCents = parseCents(amount);
  const shares = useMemo(
    () => equalShares(amountCents ?? 0, participantIDs.length),
    [amountCents, participantIDs.length],
  );
  const splitRecords = useMemo(
    () =>
      participantIDs.map((personID, index) => {
        const value = Number((splitValues[personID] ?? "").replace(",", "."));
        if (splitType === "equal")
          return { personId: personID, amountCents: shares[index] ?? 0 };
        if (splitType === "percentage")
          return {
            personId: personID,
            percentage: parseCents(splitValues[personID] ?? "") ?? 0,
          };
        if (splitType === "shares")
          return {
            personId: personID,
            shares: Number.isSafeInteger(value) ? value : 0,
          };
        return {
          personId: personID,
          amountCents: parseCents(splitValues[personID] ?? "") ?? 0,
        };
      }),
    [participantIDs, shares, splitType, splitValues],
  );
  const itemizedSplitRecords = useMemo(
    () => itemizedSplits(items.map((item) => ({
      assignedTo: item.assignedTo,
      amountCents: parseCents(item.amount) ?? 0,
    })), participantIDs),
    [items, participantIDs],
  );
  const splitTotal =
    splitType === "percentage"
      ? splitRecords.reduce(
          (total, split) => total + (split.percentage ?? 0),
          0,
        )
      : splitType === "shares"
        ? splitRecords.reduce((total, split) => total + (split.shares ?? 0), 0)
        : splitType === "itemized"
          ? (itemizedSplitRecords ?? []).reduce((total, split) => total + split.amountCents, 0)
        : splitRecords.reduce(
            (total, split) => total + (split.amountCents ?? 0),
            0,
          );
  const itemTotal = items.reduce(
    (total, item) => total + (parseCents(item.amount) ?? 0),
    0,
  );
  const splitValid =
    participantIDs.length >= 1 &&
    (splitType === "itemized"
      ? itemTotal === amountCents &&
        itemizedSplitRecords !== null &&
        items.every(
          (item) =>
            item.name.trim() &&
            parseCents(item.amount) !== null &&
            (parseCents(item.amount) ?? 0) > 0 &&
            (!item.assignedTo || participantIDs.includes(item.assignedTo)),
        )
      : splitType === "percentage"
        ? splitTotal === 10000 && participantIDs.every((id) => parseCents(splitValues[id] ?? "") !== null)
        : splitType === "shares"
          ? splitTotal > 0 &&
            splitRecords.every((split) => (split.shares ?? 0) > 0)
          : splitTotal === amountCents && (splitType === "equal" || participantIDs.every((id) => parseCents(splitValues[id] ?? "") !== null)));
  const isValid =
    Boolean(description.trim()) &&
    amountCents !== null &&
    amountCents > 0 &&
    splitValid;

  const equalShareLabel =
    splitType === "equal" && participantIDs.length > 0 && amountCents !== null && amountCents > 0
      ? `${formatMoney(shares[0] ?? 0, currency)} each`
      : undefined;
  const splitSummaryTitle =
    splitType === "equal"
      ? `Equally between ${participantIDs.length} ${participantIDs.length === 1 ? "person" : "people"}`
      : splitType === "exact"
        ? `Custom amounts between ${participantIDs.length} people`
        : splitType === "percentage"
          ? `Custom percentages between ${participantIDs.length} people`
          : splitType === "shares"
            ? `Custom shares between ${participantIDs.length} people`
            : `Itemized between ${participantIDs.length} people`;
  const splitValidationMessage = (() => {
    if (participantIDs.length < 1) return "Select at least one person.";
    if (amountCents === null || amountCents <= 0) return "Enter an amount before checking the split.";
    if (
      (splitType === "exact" || splitType === "percentage") &&
      !participantIDs.every((id) => parseCents(splitValues[id] ?? "") !== null)
    ) {
      return `Enter ${splitType === "percentage" ? "a percentage" : "an amount"} for every selected person.`;
    }
    if (splitType === "percentage" && splitTotal !== 10000) {
      const difference = 10000 - splitTotal;
      return `${Math.abs(difference / 100).toFixed(2)}% ${difference > 0 ? "still needs to be assigned" : "is over 100%"}.`;
    }
    if (splitType === "shares" && splitRecords.some((split) => (split.shares ?? 0) <= 0)) {
      return "Give every selected person at least one share.";
    }
    if (splitType === "itemized" && itemTotal !== amountCents) {
      return `${formatMoney(Math.abs(amountCents - itemTotal), currency)} ${itemTotal < amountCents ? "still needs to be assigned" : "is over the expense total"}.`;
    }
    if (splitType === "exact" && splitTotal !== amountCents) {
      return `${formatMoney(Math.abs(amountCents - splitTotal), currency)} ${splitTotal < amountCents ? "still needs to be assigned" : "is over the expense total"}.`;
    }
    if (!splitValid) return "Check the split details before saving.";
    return "Fully assigned.";
  })();
  const validationIssue = !description.trim()
    ? "Add what the expense was for."
    : amountCents === null || amountCents <= 0
      ? "Enter an amount greater than zero."
      : participantIDs.length < 1
        ? "Select at least one person."
        : !splitValid
          ? splitValidationMessage
          : "";
  const submitLabel =
    amountCents !== null && amountCents > 0
      ? `Add ${formatMoney(amountCents, currency)} expense`
      : "Add expense";

  function changeGroup(nextGroupID: string) {
    const nextGroup = findGroup(data, nextGroupID);
    const nextMembers = nextGroup
      ? Array.from(new Set([data.currentUser.id, ...nextGroup.memberIds]))
      : Array.from(
          new Set([data.currentUser.id, ...data.people.map((person) => person.id)]),
        );
    const nextPayerID = nextMembers.includes(paidByID)
      ? paidByID
      : data.currentUser.id;

    setGroupID(nextGroupID);
    setPaidByID(nextPayerID);
    setPayerQuery("");
    setSplitQuery("");
    setSplitValues({});
    setSplitType("equal");
    setSplitConfigurationOpen(false);
    if (nextGroup) {
      setParticipantIDs(
        nextMembers.filter((id) => id !== data.currentUser.id),
      );
    } else {
      setParticipantIDs((current) =>
        current.filter((id) => nextMembers.includes(id)),
      );
    }
    setItems((current) =>
      current.map((item) =>
        item.assignedTo && !nextMembers.includes(item.assignedTo)
          ? { ...item, assignedTo: "" }
          : item,
      ),
    );
  }

  function toggleParticipant(id: string) {
    if (participantIDs.includes(id)) {
      setItems((current) => current.map((item) => item.assignedTo === id ? { ...item, assignedTo: "" } : item));
    }
    setParticipantIDs((current) =>
      current.includes(id)
        ? current.filter((memberID) => memberID !== id)
        : [...current, id],
    );
  }

  function updateSplitValue(personID: string, nextValue: string) {
    setSplitValues((current) => ({ ...current, [personID]: nextValue }));
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    setSubmitAttempted(true);
    if (!isValid || amountCents === null) {
      if (participantIDs.length < 1 || !splitValid) {
        setSplitConfigurationOpen(true);
      }
      setError(validationIssue || "Check the expense details before saving.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        notes: notes.trim(),
        description: description.trim(),
        amountCents,
        currency,
        occurredAt: combineTransactionDateTime(expenseDate, expenseTime),
        occurredTimeSet: Boolean(expenseTime),
        groupId: groupID || undefined,
        paidById: paidByID,
        participantIds: participantIDs,
        splitType,
        splits: splitType === "itemized" ? itemizedSplitRecords ?? [] : splitRecords,
      });
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "We could not save that expense.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      description="Add the amount, then confirm who paid and how it should be split."
      onClose={onClose}
      closeDisabled={saving}
      size="lg"
      title="Add an expense"
      footer={<div {...stylex.props(styles.footerActions)}>
        <Button disabled={saving} onClick={onClose} variant="secondary">Cancel</Button>
        <Button disabled={saving || !isValid} form={formID} type="submit">
          {saving ? "Adding expense…" : submitLabel}<Icon {...stylex.props(styles.s44)} name="arrow-right" />
        </Button>
      </div>}
    >
      <form id={formID} onSubmit={submit}>
      <fieldset {...stylex.props(styles.s0)} disabled={saving}>
        <h3 {...stylex.props(styles.sectionTitle)}>Expense details</h3>
        <div {...stylex.props(styles.s1)}>
          <label {...stylex.props(styles.s2)}>
            <span {...stylex.props(styles.s3)}>
              What was it for?
            </span>
            <div {...stylex.props(styles.s4)} data-dropdown onBlur={(event) => { if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) setDescriptionOpen(false) }}>
              <input
                aria-controls="expense-description-options"
                aria-expanded={descriptionOpen}
                {...stylex.props(styles.s5, styles.controlSurface)}
                data-dialog-autofocus
                maxLength={120}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setDescriptionOpen(true);
                }}
                onFocus={() => setDescriptionOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && descriptionSuggestions[0]) {
                    event.preventDefault();
                    setDescription(descriptionSuggestions[0]);
                    setDescriptionOpen(false);
                  }
                }}
                placeholder="Search or enter a restaurant, merchant, or expense…"
                value={description}
              />
              {descriptionOpen && descriptionSuggestions.length > 0 && (
                <div
                  {...stylex.props(styles.s6)}
                  id="expense-description-options"
                  role="listbox"
                >
                  {descriptionSuggestions.map((value, index) => (
                    <button
                      aria-selected={description === value}
                      {...stylex.props(expenseDialogStyles.option, index === 0 ? expenseDialogStyles.optionSelected : expenseDialogStyles.optionIdle)}
                      key={value}
                      onClick={() => {
                        setDescription(value);
                        setDescriptionOpen(false);
                      }}
                      role="option"
                      type="button"
                    >
                      {value}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {submitAttempted && !description.trim() && (
              <span {...stylex.props(styles.fieldError)}>Add what the expense was for.</span>
            )}
          </label>
          <label {...stylex.props(styles.s2)}>
            <span {...stylex.props(styles.s3)}>
              Amount
            </span>
            <div {...stylex.props(styles.s7, styles.controlSurface)}>
              <MoneyInput
                {...stylex.props(styles.s8)}
                aria-label="Expense amount"
                inputMode="decimal"
                style={{ borderWidth: 0, boxShadow: 'none', borderRadius: 0 }}
                onValueChange={setAmount}
                placeholder="0.00"
                value={amount}
              />
              <select
                aria-label="Expense currency"
                {...stylex.props(styles.s9)}
                onChange={(event) => setCurrency(event.target.value)}
                value={currency}
              >
                {currencyOptions(data.currentUser.currency).map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            {submitAttempted && (amountCents === null || amountCents <= 0) && (
              <span {...stylex.props(styles.fieldError)}>Enter an amount greater than zero.</span>
            )}
          </label>
        </div>

        <div {...stylex.props(styles.contextGrid)}>
          <label {...stylex.props(styles.s2)}>
            <span {...stylex.props(styles.s3)}>Group</span>
            <Select
              aria-label="Expense group"
              onChange={(event) => changeGroup(event.target.value)}
              value={groupID}
            >
              <option value="">No group</option>
              {data.groups.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.name}</option>
              ))}
            </Select>
          </label>

          <div style={{ gridColumn: "1 / -1" }}>
            <TransactionDateTimeField
              date={expenseDate}
              dateLabel="Expense date"
              onDateChange={setExpenseDate}
              onTimeChange={setExpenseTime}
              time={expenseTime}
              timeLabel="Expense time"
            />
          </div>
        </div>

        <>
          <h3 {...stylex.props(styles.sectionTitle)}>People and split</h3>
          <fieldset {...stylex.props(styles.s28)}>
            <legend {...stylex.props(styles.s13)}>
              Paid by
            </legend>
            <div {...stylex.props(styles.s4)} data-dropdown onBlur={(event) => { if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) setPayerOpen(false) }}>
              <button
                aria-controls="payer-options"
                aria-expanded={payerOpen}
                {...stylex.props(styles.summaryButton, styles.controlSurface, payerOpen && styles.summaryButtonOpen)}
                onClick={() => setPayerOpen((open) => !open)}
                type="button"
              >
                <Avatar
                  person={
                    selectedPayer ?? { id: paidByID, name: "Someone", color: 1 }
                  }
                  size={34}
                />
                <span {...stylex.props(styles.summaryMain)}>
                  <span {...stylex.props(styles.summaryTitle)}>
                    {paidByID === data.currentUser.id ? "You" : selectedPayer?.name ?? "Someone"}
                  </span>
                  <span {...stylex.props(styles.summaryMeta)}>Tap to change who paid</span>
                </span>
                <Icon {...stylex.props(styles.summaryChevron)} name="chevron" />
              </button>
              {payerOpen && (
                <div
                  {...stylex.props(styles.s6)}
                  id="payer-options"
                >
                  <SearchInput
                    aria-label="Search who paid"
                    autoComplete="off"
                    autoFocus
                    onChange={(event) => setPayerQuery(event.target.value)}
                    placeholder="Search people"
                    value={payerQuery}
                  />
                  <div role="listbox">
                    {filteredMembers.length === 0 ? (
                      <p {...stylex.props(styles.s17)}>
                        No people found.
                      </p>
                    ) : (
                      filteredMembers.map((memberID) => {
                        const person = findPerson(data, memberID);
                        if (!person) return null;
                        const selected = paidByID === memberID;
                        return (
                          <button
                            aria-selected={selected}
                            {...stylex.props(expenseDialogStyles.option, selected ? expenseDialogStyles.optionSelected : expenseDialogStyles.optionIdle)}
                            key={memberID}
                            onClick={() => {
                              setPaidByID(memberID);
                              setPayerQuery("");
                              setPayerOpen(false);
                            }}
                            role="option"
                            type="button"
                          >
                            <Avatar person={person} size={29} />
                            <span {...stylex.props(styles.s18)}>
                              {memberID === data.currentUser.id
                                ? "You"
                                : person.name}
                            </span>
                            {selected && (
                              <Icon
                                {...stylex.props(styles.s19)}
                                name="check"
                              />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </fieldset>

          <fieldset {...stylex.props(styles.s28)}>
            <legend {...stylex.props(styles.s13)}>Split</legend>
            <button
              aria-expanded={splitConfigurationOpen}
              {...stylex.props(styles.summaryButton, styles.controlSurface, splitConfigurationOpen && styles.summaryButtonOpen)}
              onClick={() => setSplitConfigurationOpen((open) => !open)}
              type="button"
            >
              <Icon name="people" />
              <span {...stylex.props(styles.summaryMain)}>
                <span {...stylex.props(styles.summaryTitle)}>{splitSummaryTitle}</span>
                <span {...stylex.props(styles.summaryMeta)}>
                  {equalShareLabel ?? "Tap to change people or split method"}
                </span>
              </span>
              <Icon {...stylex.props(styles.summaryChevron)} name="chevron" />
            </button>
            {submitAttempted && participantIDs.length < 1 && (
              <p {...stylex.props(styles.fieldError)}>Select at least one person.</p>
            )}
          </fieldset>

          {splitConfigurationOpen && (
            <div {...stylex.props(styles.splitConfiguration)}>
          <fieldset {...stylex.props(styles.s28)}>
            <legend {...stylex.props(styles.s13)}>
              Split with
            </legend>
            <div {...stylex.props(styles.s4)} data-dropdown onBlur={(event) => { if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) setSplitOpen(false) }}>
              <SearchInput
                aria-label="Search people to split with"
                onChange={(event) => {
                  setSplitQuery(event.target.value);
                  setSplitOpen(true);
                }}
                onFocus={() => setSplitOpen(true)}
                placeholder={
                  participantIDs.length
                    ? `${participantIDs.length} people selected`
                    : "Search and select people"
                }
                value={splitQuery}
              />
              {splitOpen && (
                <div
                  {...stylex.props(styles.s22)}
                  role="listbox"
                >
                  {filteredSplitMembers.length === 0 ? (
                    <p {...stylex.props(styles.s17)}>
                      No other people found.
                    </p>
                  ) : (
                    filteredSplitMembers.map((memberID) => (
                      <button
                        aria-selected={participantIDs.includes(memberID)}
                        {...stylex.props(expenseDialogStyles.option, participantIDs.includes(memberID) ? expenseDialogStyles.optionSelected : expenseDialogStyles.participantIdle)}
                        key={memberID}
                        onClick={() => toggleParticipant(memberID)}
                        role="option"
                        type="button"
                      >
                        <Avatar
                          person={findPerson(data, memberID)!}
                          size={28}
                        />
                        <span {...stylex.props(styles.s23)}>
                          {memberID === data.currentUser.id
                            ? "You"
                            : findPerson(data, memberID)?.name}
                        </span>
                        {participantIDs.includes(memberID) && (
                          <Icon
                            {...stylex.props(styles.s19)}
                            name="check"
                          />
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <div {...stylex.props(styles.s24)}>
              {participantIDs.map((id) => (
                <span
                  {...stylex.props(styles.s25)}
                  key={id}
                >
                  {id === data.currentUser.id
                    ? "You"
                    : findPerson(data, id)?.name}
                  <button
                    aria-label="Remove selected person"
                    {...stylex.props(styles.s26)}
                    onClick={() => toggleParticipant(id)}
                    type="button"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <p {...stylex.props(styles.s27)}>
              Select at least one person to split this expense.
            </p>
          </fieldset>
          <fieldset {...stylex.props(styles.s28)}>
            <legend {...stylex.props(styles.s13)}>
              Split method
            </legend>
            <SegmentedControl
              onChange={setSplitType}
              options={[
                { value: "equal", label: "Equal" },
                { value: "exact", label: "Amounts" },
                { value: "percentage", label: "%" },
                { value: "shares", label: "Shares" },
                { value: "itemized", label: "Items" },
              ]}
              value={splitType}
            />
            {splitType === "itemized" ? (
              <div {...stylex.props(styles.s30)}>
                {items.map((item) => (
                  <div {...stylex.props(styles.itemRow)} key={item.id}>
                    <Input
                      aria-label="Item name"
                      {...stylex.props(styles.s32, styles.itemName)}
                      onChange={(event) =>
                        setItems((current) =>
                          current.map((entry) =>
                            entry.id === item.id
                              ? { ...entry, name: event.target.value }
                              : entry,
                          ),
                        )
                      }
                      placeholder="Item name"
                      value={item.name}
                    />
                    <Select
                      aria-label="Who is this item for"
                      {...stylex.props(styles.s46)}
                      onChange={(event) => setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, assignedTo: event.target.value } : entry))}
                      value={item.assignedTo}
                    >
                      <option value="">Split equally</option>
                      {participantIDs.map((personID) => <option key={personID} value={personID}>{personID === data.currentUser.id ? "You" : findPerson(data, personID)?.name}</option>)}
                    </Select>
                    <MoneyInput
                      aria-label="Item amount"
                      {...stylex.props(styles.s33)}
                      inputMode="decimal"
                      onValueChange={(value) =>
                          setItems((current) =>
                            current.map((entry) =>
                              entry.id === item.id
                                ? { ...entry, amount: value }
                                : entry,
                            ),
                          )
                      }
                      placeholder="0.00"
                      value={item.amount}
                    />
                    <button
                      aria-label="Remove item"
                      {...stylex.props(styles.s34)}
                      disabled={items.length === 1}
                      onClick={() =>
                        setItems((current) =>
                          current.filter((entry) => entry.id !== item.id),
                        )
                      }
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  {...stylex.props(styles.s35)}
                  onClick={() =>
                    setItems((current) => [
                      ...current,
                      { id: Math.max(...current.map((item) => item.id)) + 1, name: "", amount: "", assignedTo: "" },
                    ])
                  }
                  type="button"
                >
                  + Add item
                </button>
              </div>
            ) : (
              splitType !== "equal" && (
                <div {...stylex.props(styles.s30)}>
                  {participantIDs.map((id) => (
                    <label {...stylex.props(styles.s31)} key={id}>
                      <span {...stylex.props(styles.s36)}>
                        {id === data.currentUser.id
                          ? "You"
                          : findPerson(data, id)?.name}
                      </span>
                      <div {...stylex.props(styles.s37, styles.controlSurface)}>
                        {splitType === "exact" ? <MoneyInput onValueChange={(value) => updateSplitValue(id, value)} placeholder="0.00" style={{ borderWidth: 0, boxShadow: 'none', minWidth: 0, flex: '1 1 0%', textAlign: 'right' }} value={splitValues[id] ?? ""} /> :
                        <input
                          {...stylex.props(styles.s38)}
                          inputMode={splitType === "shares" ? "numeric" : "decimal"}
                          onChange={(event) => updateSplitValue(id, event.target.value)}
                          placeholder={splitType === "shares" ? "1" : "0"}
                          type="text"
                          value={splitValues[id] ?? ""}
                        />}
                        <span {...stylex.props(styles.s39)}>
                          {splitType === "exact"
                            ? currency
                            : splitType === "percentage"
                              ? "%"
                              : "shares"}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )
            )}
            <p {...stylex.props(styles.s27)}>
              {splitType === "equal"
                ? "Split the total evenly."
                : splitType === "exact"
                  ? `Entered ${((splitTotal ?? 0) / 100).toFixed(2)} ${currency} · Remaining ${(((amountCents ?? 0) - splitTotal) / 100).toFixed(2)} ${currency}`
                  : splitType === "percentage"
                    ? `Percent assigned ${((splitTotal ?? 0) / 100).toFixed(2)}% · Remaining ${(100 - (splitTotal ?? 0) / 100).toFixed(2)}%`
                      : splitType === "itemized"
                      ? `Items ${((itemTotal ?? 0) / 100).toFixed(2)} ${currency} · Remaining ${(((amountCents ?? 0) - itemTotal) / 100).toFixed(2)} ${currency}`
                      : "Each person needs at least one share."}
            </p>
            {(splitType === "exact" || splitType === "itemized") && (
              <button {...stylex.props(styles.s35)} disabled={(splitType === "itemized" ? itemTotal : splitTotal) <= 0} type="button" onClick={() => setAmount(((splitType === "itemized" ? itemTotal : splitTotal) / 100).toFixed(2))}>
                Use {splitType === "itemized" ? "item" : "split"} total
              </button>
            )}
            {(splitType === "equal" || (splitType === "itemized" && itemizedSplitRecords)) && (
              <div {...stylex.props(styles.splitSummary)} aria-label="Each person's share">
                {(splitType === "equal" ? splitRecords : itemizedSplitRecords ?? []).map((split) => (
                  <div {...stylex.props(styles.summaryRow)} key={split.personId}>
                    <span>{split.personId === data.currentUser.id ? "You" : findPerson(data, split.personId)?.name}</span>
                    <strong>{formatMoney(split.amountCents ?? 0, currency)}</strong>
                  </div>
                ))}
              </div>
            )}
            <p {...stylex.props(styles.splitStatus, splitValid ? styles.splitStatusValid : styles.splitStatusInvalid)}>
              {splitValid ? "✓ Fully assigned." : splitValidationMessage}
            </p>
          </fieldset>
            </div>
          )}
        </>

        <details {...stylex.props(styles.extra)}><summary {...stylex.props(styles.extraSummary)}>Extra details<Icon name="chevron" /></summary><div {...stylex.props(styles.extraContent)}><FormField label="Description (optional)"><Textarea maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder="Add details about this expense" value={notes} /></FormField></div></details>

        {error && (
          <p
            {...stylex.props(styles.s40)}
            role="alert"
          >
            {error}
          </p>
        )}
        </fieldset>
      </form>
    </Dialog>
  );
}
